import { useMemo } from 'react';
import {
  logOutreachEvent,
  readToneLabels,
  useCurrentUserProfile,
  useCustomTemplates,
  type ClientRow,
  type ClientStatsView,
} from '@busy/api';
import {
  defaultToneLabel,
  TEMPLATE_TONES,
  buildDraftVars,
  generateDraftText,
  type TemplateLanguage,
  type TemplateTone,
} from '@busy/core';
import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useUI } from '../store/ui';
import { openSms } from '../lib/sms';
import { useT } from '../lib/i18n';

// Draft-text picker. Reached by tapping the gold "Text" pill on a client detail
// modal. Builds a rendered draft per tone using the user's templates (custom or
// system default) + the client's stats, then opens the user's SMS app pre-filled
// when a tone is picked.
//
// Design (per v1 simplification): one editable template per tone — Casual / Pro /
// Urgent (or whatever the barber renamed them to). Same template applies to every
// client with their data substituted in. CRM-not-marketing: Busy never sends, the
// barber does.
export function TextDraftPicker({
  client,
  stats,
  onClose,
}: {
  client: ClientRow;
  stats: ClientStatsView | undefined;
  onClose: () => void;
}) {
  const profile = useCurrentUserProfile();
  const customs = useCustomTemplates();
  const language = useUI((s) => s.language) as TemplateLanguage;
  const t = useT();

  const customLookup = useMemo(
    () =>
      (tone: TemplateTone, lang: TemplateLanguage): string | null =>
        customs.data?.[tone]?.[lang] ?? null,
    [customs.data]
  );

  const toneLabels = useMemo(() => readToneLabels(profile.data ?? null), [profile.data]);
  const labelFor = (tone: TemplateTone) => toneLabels[tone] ?? defaultToneLabel(tone, language);

  const vars = useMemo(() => {
    const signature =
      profile.data?.first_name?.trim() || (language === 'es' ? '[tu nombre]' : '[your name]');
    return buildDraftVars({
      clientId: client.id,
      firstName: client.first_name,
      daysSinceLastVisit:
        stats?.days_since_last_visit != null ? Math.round(stats.days_since_last_visit) : null,
      typicalGap: stats?.cycle_days != null ? Math.round(stats.cycle_days) : null,
      preferredService: null,
      signature,
      language,
    });
  }, [client, stats, profile.data, language]);

  const drafts = useMemo(() => {
    if (!client.phone) return null;
    return TEMPLATE_TONES.map((tone) => ({
      tone,
      body: generateDraftText({ tone, language, vars, customLookup }),
    }));
  }, [client.phone, language, vars, customLookup]);

  function handlePick(body: string, tone: TemplateTone) {
    if (!client.phone) return;
    openSms(client.phone, body);
    // Fire-and-forget event log. Captures client + tone (no body) so
    // we can correlate outreach with rebook timing later. Failures are
    // swallowed inside logOutreachEvent — never blocks the SMS open.
    void logOutreachEvent({ clientId: client.id, draftKind: tone });
    onClose();
  }

  const fullName = `${client.first_name} ${client.last_name}`.trim() || t('textDraft.thisClient');

  if (!client.phone) {
    return (
      <ModalShell open onClose={onClose} size="sm" ariaLabel={t('textDraft.aria.noPhone')}>
        <ModalCloseButton onClose={onClose} />
        <div className="p-5 pr-12 space-y-3">
          <h2 className="font-display text-2xl">{t('textDraft.noPhone.title')}</h2>
          <p className="text-sm text-ink-700 leading-relaxed">
            {t('textDraft.noPhone.body', { name: fullName })}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold"
          >
            {t('textDraft.noPhone.cta')}
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell open onClose={onClose} size="lg" ariaLabel={t('textDraft.aria', { name: fullName })}>
      <ModalCloseButton onClose={onClose} />
      <div className="p-5 pr-12 border-b border-line">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1">
          {t('textDraft.eyebrow')}
        </div>
        <h2 className="font-display text-2xl">{fullName}</h2>
        <p className="text-[13px] text-ink-500 mt-0.5 break-all">{client.phone}</p>
      </div>

      <div className="p-5 space-y-3 max-h-[60dvh] overflow-y-auto">
        {customs.loading ? (
          <div className="space-y-2">
            <div className="h-32 rounded-xl bg-bg-alt animate-pulse" />
            <div className="h-32 rounded-xl bg-bg-alt animate-pulse" />
            <div className="h-32 rounded-xl bg-bg-alt animate-pulse" />
          </div>
        ) : (
          drafts?.map(({ tone, body }) => (
            <button
              key={tone}
              type="button"
              onClick={() => handlePick(body, tone)}
              className="w-full text-left rounded-xl border border-line bg-surface p-4 hover:border-navy-900 dark:hover:border-gold-500 transition-colors group"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-display text-lg text-navy-900 dark:text-gold-500 leading-tight min-w-0">
                  {labelFor(tone)}
                </div>
                <span className="inline-flex items-center gap-1 text-[12px] font-bold text-navy-900 dark:text-gold-500 group-hover:translate-x-0.5 transition-transform shrink-0">
                  {t('textDraft.sendVia')}
                  <ChevronIcon className="w-3.5 h-3.5" />
                </span>
              </div>
              <p className="text-[14px] leading-relaxed text-ink-900 whitespace-pre-wrap break-words">
                {body}
              </p>
            </button>
          ))
        )}

        <p className="text-[11px] text-ink-500 text-center pt-2">
          {t('textDraft.footer', {
            lang: t(language === 'es' ? 'textDraft.lang.es' : 'textDraft.lang.en'),
          })}
        </p>
      </div>
    </ModalShell>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
